import { AspectRatio } from '@/components/AspectRatioDropdown/AspectRatioDropdown';
import { Page } from '@/pages/AdvancedEditor/AdvancedEditor';
import { PolotnoStoreActivePageKeys, setPageProperty } from '@/pages/AdvancedEditor/hooks';
import { PolotnoStoreKeys, setStoreProperty } from '@/pages/AdvancedEditor/hooks/polotno-store/usePolotnoState';
import { uploadFile } from '@/services/api/studioApi';
import { CropMode } from '@/utils/types';
import { StoreType } from 'polotno/model/store';
import { updateCanvasSize } from '../../../../hooks/useCrop/updateCanvasSize';
import { getFileMetadata } from '../../media/MediaList/MediaUpload/MediaUpload';
import { FigmaModalFormData } from './FigmaModal';

interface FigmaColor {
    r: number;
    g: number;
    b: number;
    a: number;
}

interface FigmaFill {
    type: string;
    color?: FigmaColor;
    opacity?: number;
    visible?: boolean;
}

interface FigmaBoundingBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface FigmaNode {
    id: string;
    name: string;
    type: string;
    absoluteBoundingBox?: FigmaBoundingBox;
    characters?: string;
    fills?: FigmaFill[];
    children?: FigmaNode[];
    strokes?: any[];
    cornerRadius?: number;
    backgroundColor?: FigmaColor;
    style?: {
        fontFamily?: string;
        fontWeight?: number;
        fontSize?: number;
        textAlignHorizontal?: string;
        textAlignVertical?: string;
    };
    styleOverrideTable?: Record<string, { fontFamily: string }>;
}

// Function to convert Figma color to CSS color
const figmaColorToCss = (color?: FigmaColor): string => {
    if (!color) return 'transparent';

    const r = Math.round(color.r * 255);
    const g = Math.round(color.g * 255);
    const b = Math.round(color.b * 255);
    const a = color.a || 1;

    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export class FigmaImporter {
    private figmaToken = 'figd_MafNbXGCLeN0rWr5aMz62LbOdjaPNkex6iciyIkJ';

    private figmaFileKey: string = '';
    private figmaNodeId: string = '';
    private x = 0;
    private y = 0;
    private rootNode: FigmaNode | null = null;

    constructor(
        private store: StoreType,
        private uploadMedia: ({ src, metadata }: { src: string; metadata: Record<string, string> }) => Promise<{ source_url: string }>
    ) {}

    async getRootNode() {
        this.rootNode = await this.fetchFigmaNode();
    }

    private setCanvasSize(figmaNode: FigmaNode) {
        if (figmaNode.absoluteBoundingBox) {
            const width = figmaNode.absoluteBoundingBox.width;
            const height = figmaNode.absoluteBoundingBox.height;

            if (width === 1080 && height === 1920) {
                updateCanvasSize({ cropMode: CropMode.Vertical, store: this.store });
                setStoreProperty(this.store, PolotnoStoreKeys.cropSettings)(CropMode.Vertical);
                setStoreProperty(this.store, PolotnoStoreKeys.aspectRatio)(AspectRatio.Portrait);
            }

            if (width === 1920 && height === 1080) {
                updateCanvasSize({ cropMode: CropMode.Wide, store: this.store });
                setStoreProperty(this.store, PolotnoStoreKeys.cropSettings)(CropMode.Wide);
                setStoreProperty(this.store, PolotnoStoreKeys.aspectRatio)(AspectRatio.Landscape);
            }

            if (width === 1080 && height === 1080) {
                updateCanvasSize({ cropMode: CropMode.Square, store: this.store });
                setStoreProperty(this.store, PolotnoStoreKeys.cropSettings)(CropMode.Square);
                setStoreProperty(this.store, PolotnoStoreKeys.aspectRatio)(AspectRatio.Square);
            }
        }
    }

    private async fetchFigmaNode(): Promise<FigmaNode | null> {
        console.log('Fetching Figma node:', this.figmaNodeId);
        const figmaApiUrl = `https://api.figma.com/v1/files/${this.figmaFileKey}/nodes?ids=${this.figmaNodeId.split(':').join('-')}`;

        console.log('Figma API URL:', figmaApiUrl);

        try {
            const response = await fetch(figmaApiUrl, {
                headers: { 'X-Figma-Token': this.figmaToken },
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`Failed to fetch Figma selection: ${response.status} ${response.statusText}`);
            }

            const figmaData = await response.json();

            if (figmaData.nodes) {
                console.log('Available nodes:', Object.keys(figmaData.nodes));
            }

            const formattedNodeId = this.figmaNodeId.split('-').join(':');
            if (!figmaData.nodes || !figmaData.nodes[formattedNodeId]) {
                console.error('Node not found in response. Available nodes:', figmaData.nodes ? Object.keys(figmaData.nodes) : 'none');
                throw new Error(`Node '${formattedNodeId}' not found in Figma response.`);
            }

            console.log('Figma root node:', figmaData.nodes[formattedNodeId].document);
            return figmaData.nodes[formattedNodeId].document;
        } catch (error) {
            console.error('Error fetching Figma selection:', error);
            return null;
        }
    }

    private setInitialCoordinates(figmaNode: FigmaNode) {
        if (figmaNode.absoluteBoundingBox) {
            this.x = figmaNode.absoluteBoundingBox.x;
            this.y = figmaNode.absoluteBoundingBox.y;
        }
    }

    private convertToPolotnoElement(figmaNode: FigmaNode, imageUrl?: string): any {
        // Calculate relative position to parent
        const x = (figmaNode.absoluteBoundingBox?.x || 0) - this.x;
        const y = (figmaNode.absoluteBoundingBox?.y || 0) - this.y;

        // Base properties for all element types
        const baseElement = {
            x: x,
            y: y,
            width: figmaNode.absoluteBoundingBox?.width || 100,
            height: figmaNode.absoluteBoundingBox?.height || 100,
            name: figmaNode.name,
        };

        // Handle different element types
        switch (figmaNode.type) {
            case 'TEXT':
                return {
                    ...baseElement,
                    type: 'text',
                    text: figmaNode.characters || '',
                    fontFamily: figmaNode.styleOverrideTable
                        ? Object.values(figmaNode.styleOverrideTable)?.[0]?.fontFamily || figmaNode.style?.fontFamily
                        : figmaNode.style?.fontFamily || 'Arial',
                    fontSize: figmaNode.style?.fontSize || 16,
                    fontWeight: `${figmaNode.style?.fontWeight}` || '400',
                    textAlign: figmaNode.style?.textAlignHorizontal?.toLowerCase() || 'left',
                    verticalAlign: figmaNode.style?.textAlignVertical?.toLowerCase() || 'top',
                    fill: figmaNode.fills?.[0] ? figmaColorToCss(figmaNode.fills[0].color) : '#000000',
                };

            case 'RECTANGLE':
            case 'ELLIPSE':
            case 'POLYGON':
            case 'STAR':
            case 'VECTOR':
            case 'LINE':
                return {
                    ...baseElement,
                    type: 'shape',
                    fill: figmaNode.fills?.[0] ? figmaColorToCss(figmaNode.fills[0].color) : 'transparent',
                    stroke: figmaNode.strokes?.[0] ? figmaColorToCss(figmaNode.strokes[0].color) : 'transparent',
                    strokeWidth: figmaNode.strokes?.[0]?.thickness || 0,
                    cornerRadius: figmaNode.cornerRadius || 0,
                    shape: figmaNode.type.toLowerCase(),
                };

            case 'FRAME':
            case 'COMPONENT':
            case 'INSTANCE':
            case 'GROUP':
                // If we have an image URL for this node, use it
                if (imageUrl) {
                    return {
                        ...baseElement,
                        type: 'image',
                        src: imageUrl,
                    };
                }
                // Otherwise, return a container element
                return {
                    ...baseElement,
                    type: 'group',
                    // Children will be added separately
                };

            default:
                // For unsupported types, create a basic rectangle as a placeholder
                return {
                    ...baseElement,
                    type: 'shape',
                    fill: '#CCCCCC',
                    stroke: '#999999',
                    strokeWidth: 1,
                    cornerRadius: 0,
                    shape: 'rectangle',
                };
        }
    }

    private async processNode(figmaNode: FigmaNode, parentIndex?: string): Promise<any[]> {
        const elements: any[] = [];

        // Skip the node if it doesn't have a bounding box
        if (!figmaNode.absoluteBoundingBox) {
            console.log(`Node ${figmaNode.id} has no bounding box, skipping`);
            return elements;
        }

        // For complex nodes, fetch image
        let imageUrl: string | undefined;
        if (['FRAME', 'COMPONENT', 'INSTANCE', 'GROUP', 'VECTOR'].includes(figmaNode.type)) {
            const images = await this.fetchFigmaImages([figmaNode.id]);
            imageUrl = images[figmaNode.id];
        }

        // Convert the current node to a Polotno element
        const polotnoElement = this.convertToPolotnoElement(figmaNode, imageUrl);
        elements.push(polotnoElement);

        // Process children recursively
        if (figmaNode.children && figmaNode.children.length > 0) {
            for (const [index, child] of figmaNode.children.entries()) {
                const indexToDisplay = `${parentIndex ? `${parentIndex}-` : ''}${index + 1}`;

                console.log(`Processing child ${indexToDisplay}`);
                const childElements = await this.processNode(child, indexToDisplay);
                elements.push(...childElements);
            }
        }

        return elements;
    }

    private async fetchFigmaImages(nodeIds: string[]): Promise<Record<string, string>> {
        if (nodeIds.length === 0) return {};

        const url = `https://api.figma.com/v1/images/${this.figmaFileKey}?ids=${nodeIds.join(',')}&format=png`;

        try {
            const response = await fetch(url, {
                headers: { 'X-Figma-Token': this.figmaToken },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch Figma images: ${response.statusText}`);
            }

            const data = await response.json();
            return data.images || {};
        } catch (error) {
            console.error('Error fetching Figma images:', error);
            return {};
        }
    }

    private async uploadFigmaImage(imageUrl: string): Promise<string> {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'image.png', { type: 'image/png' });

        const metadata = await getFileMetadata(file);

        if (!metadata) {
            throw new Error('Failed to get file metadata');
        }

        const src = await uploadFile(file);
        const result = await this.uploadMedia({
            src,
            metadata: { width: metadata.width.toString(), height: metadata.height.toString() },
        });

        if (!result.source_url) {
            throw new Error('Failed to upload image');
        }

        return result.source_url;
    }

    private async addFigmaToPolotno(figmaNode: FigmaNode): Promise<void> {
        try {
            // Process the node and its children - process the main node directly
            const polotnoElements = await this.processNode(figmaNode);

            console.log('Polotno elements:', polotnoElements);

            // Add elements to Polotno
            const activePage = this.store.activePage;
            if (!activePage) {
                throw new Error('No active page in Polotno store');
            }

            // Add all elements to the Polotno page
            for (const element of polotnoElements) {
                // image upload
                if (element.type === 'image') {
                    const src = await this.uploadFigmaImage(element.src);
                    element.src = src;
                }

                activePage.addElement(element);
            }

            console.log('Figma content successfully imported to Polotno!');
            console.log(' ');
        } catch (error) {
            console.error('Error importing Figma content to Polotno:', error);
            console.log(' ');
            throw error;
        }
    }

    private async importScene() {
        await this.getRootNode();

        if (!this.rootNode) {
            throw new Error('Failed to fetch Figma node');
        }

        this.setCanvasSize(this.rootNode);
        this.setInitialCoordinates(this.rootNode);

        await this.addFigmaToPolotno(this.rootNode);
    }

    private setPageScript(page: Page, script: string) {
        setPageProperty(page, PolotnoStoreActivePageKeys.textInput)(script.replaceAll('Script: ', ''));
    }

    public async import(data: FigmaModalFormData) {
        this.figmaFileKey = data.fileKey;

        for (const [index, scene] of data.scenes.entries()) {
            this.figmaNodeId = scene.nodeId;

            // Skip adding page if scene index is 0
            if (index !== 0) {
                const page = this.store.addPage();
                this.store.selectPage(page.id);

                this.setPageScript(page, scene.script);
            } else {
                this.setPageScript(this.store.activePage, scene.script);
            }

            await this.importScene();
        }
    }
}
